import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from 'shared/components/ui/dialog';
import chatStore from 'shared/store/chatStore';
import constraintProfilesStore from 'shared/store/constraintProfilesStore';
import constraintsStore from 'shared/store/constraintsStore';
import { ConstraintProfile } from 'types/ConstraintProfileTypes';
import { ProfileCard } from './ProfileCard';
import { FinecoButton } from 'pages/assistant/components/FinecoButton';
import { clearAgentChangeIndicators } from '../../utils/clearAgentChangeIndicators';

import styles from './savedProfiles.module.scss';
import { createLogger } from 'shared/logger';

const log = createLogger('ManageProfiles');

type ManageProfilesDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const ManageProfilesDialog: React.FC<ManageProfilesDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const profilesByMode = constraintProfilesStore((s) => s.profilesByMode);
  const mode = constraintsStore((s) => s.mode);
  const schemaDefaults = constraintsStore((s) => s.predefiniti) ?? {};
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const modeProfiles = profilesByMode[mode] ?? [];

  // "Predefiniti" is not a persisted profile — it is the fallback that applies
  // when no saved profile holds the default. We surface it as a synthetic,
  // always-first placeholder card so advisors manage it like any other config
  // (set-as-default + apply) instead of hunting for the hidden "remove the
  // star elsewhere" gesture.
  const currentDefault = modeProfiles.find((p) => p.is_default) ?? null;
  const predefinitaProfile: ConstraintProfile = {
    id: '__predefinita__',
    name: t('savedProfiles.defaults'),
    mode,
    is_default: !currentDefault, // active default exactly when nothing is flagged
    constraints: schemaDefaults,
    schema_version: 0,
  };

  // Setting Predefiniti as the default just means clearing whichever saved
  // profile currently holds the flag. No-op when it is already the default —
  // the placeholder's star is locked in that state (see ProfileCard).
  const handleSetPredefinitaDefault = async () => {
    if (!currentDefault) return;
    await constraintProfilesStore
      .getState()
      .updateProfile(currentDefault.id, { is_default: false });
  };

  const handleDeleteRequest = (profileId: string) => {
    setPendingDeleteId(profileId);
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteId) return;
    const deletedId = pendingDeleteId;
    try {
      await constraintProfilesStore.getState().deleteProfile(deletedId);

      // If the deleted profile was applied, re-baseline so neither the
      // dropdown nor the thread metadata keeps pointing at something that
      // no longer exists. Predefiniti is the universal fallback — neutral
      // (no assumption about which other profile the user "wants"), and
      // consistent for both fresh chats and existing threads.
      //
      //   - Fresh chat (no thread): applyPredefiniti snaps both
      //     workingDocument and the baseline pointer to predefiniti.
      //   - Existing thread: replaceAppliedProfile(null) keeps the
      //     thread's actual constraints (workingDocument) and only moves
      //     the baseline pointer; it also flags pendingSync so the silent-
      //     sync path overwrites the dead reference in thread metadata.
      const { appliedProfile } = constraintsStore.getState();
      if (appliedProfile?.id === deletedId) {
        const currentThreadId = chatStore.getState().currentThreadId;
        if (!currentThreadId) {
          constraintsStore.getState().applyPredefiniti();
          clearAgentChangeIndicators();
        } else {
          constraintsStore.getState().replaceAppliedProfile(null);
        }
      }
    } catch (error) {
      log.error('delete constraint profile failed', { error });
    } finally {
      setPendingDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setPendingDeleteId(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t('savedProfiles.manageDialogTitle')}
        className={styles.manageDialog}
      >
        <p className={styles.manageDialogDescription}>
          {t('savedProfiles.manageDialogDescription')}
        </p>

        <div className={styles.configGrid}>
          {/* Always-first placeholder for the built-in "Predefiniti" default. */}
          <ProfileCard
            key={predefinitaProfile.id}
            isPredefinita
            profile={predefinitaProfile}
            schemaDefaults={schemaDefaults}
            onApply={() => {
              constraintsStore.getState().applyPredefiniti();
              clearAgentChangeIndicators();
              onOpenChange(false);
            }}
            onToggleDefault={handleSetPredefinitaDefault}
            onDelete={() => {}}
            onRename={() => {}}
          />

          {modeProfiles.map((profile: ConstraintProfile) => (
            <ProfileCard
              key={profile.id}
              profile={profile}
              schemaDefaults={schemaDefaults}
              onApply={(p) => {
                constraintsStore.getState().applyProfile(p);
                clearAgentChangeIndicators();
                onOpenChange(false);
              }}
              onDelete={handleDeleteRequest}
              onToggleDefault={(p) =>
                constraintProfilesStore
                  .getState()
                  .updateProfile(p.id, { is_default: !p.is_default })
              }
              onRename={(p, newName) =>
                constraintProfilesStore
                  .getState()
                  .updateProfile(p.id, { name: newName })
              }
            />
          ))}

          {modeProfiles.length === 0 && (
            <p className={styles.emptyState}>
              {t('savedProfiles.noProfiles')}
            </p>
          )}
        </div>

        <div className={styles.manageDialogFooter}>
          <FinecoButton
            click={() => onOpenChange(false)}
            copy={t('global.close')}
          />
        </div>

        {/* Delete confirmation overlay — must live inside DialogContent so it
            renders in the dialog's portal and its z-index stacks above the
            dialog backdrop instead of being trapped behind it. */}
        {pendingDeleteId && (
          <div className={styles.confirmOverlay}>
            <div className={styles.confirmBox}>
              <p className={styles.confirmText}>
                {t('savedProfiles.deleteConfirm')}
              </p>
              <div className={styles.confirmActions}>
                <FinecoButton
                  click={handleDeleteCancel}
                  copy={t('actions.cancel')}
                  variant="white"
                />
                <FinecoButton
                  click={handleDeleteConfirm}
                  copy={t('global.delete')}
                />
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
